import { createAsyncThunk } from '@reduxjs/toolkit';

import { tracker } from 'app-components/utils/core';

import { accountsActions, accountsTypes } from '../accounts';
import { isVehicleAccount } from '../accounts/accountsSelectors';
import type { VehicleAccountProps } from '../accounts/accountsTypes';
import { vehicleApi } from './vehicleApi';
import type { VehicleFormManager } from './vehicleForm';
import {
  type KbbFullVehicle,
  type KbbValuation,
  type KbbVehicleMake,
  type KbbVehicleModel,
  type KbbVehicleTrimResult,
  VEHICLE_REDUCER_KEY,
  VehicleConditionLabel,
  type VehicleFormState,
  type VehiclePreview,
  type VehiclePriceType,
} from './vehicleTypes';

export const fetchKbbMakes = createAsyncThunk<KbbVehicleMake[], string>(
  `${VEHICLE_REDUCER_KEY}/fetchKbbMakes`,
  (year) => vehicleApi.getKbbMakes({ year }),
);

export const fetchKbbModels = createAsyncThunk<KbbVehicleModel[], { year: string; makeId: number }>(
  `${VEHICLE_REDUCER_KEY}/fetchKbbModels`,
  (params) => vehicleApi.getKbbModels(params),
);

export const fetchKbbTrims = createAsyncThunk<KbbVehicleTrimResult, { year: string; makeId: number; modelId: number }>(
  `${VEHICLE_REDUCER_KEY}/fetchKbbTrims`,
  (params) => vehicleApi.getKbbTrims(params),
);

export const fetchKbbFullVehicle = createAsyncThunk<
  KbbFullVehicle[],
  { year: string; makeId: number; modelId: number; modelYearId: number; trimId: number }
>(`${VEHICLE_REDUCER_KEY}/fetchKbbFullVehicle`, (params) => vehicleApi.getKbbFullVehicle(params));

export const fetchKbbFullVehicleByVin = createAsyncThunk<KbbFullVehicle[], string>(
  `${VEHICLE_REDUCER_KEY}/fetchKbbFullVehicleByVin`,
  (vin) => vehicleApi.getKbbFullVehicleByVin({ vin }),
);

export const fetchKbbFullVehicleByVehicleId = createAsyncThunk<KbbFullVehicle, number>(
  `${VEHICLE_REDUCER_KEY}/fetchKbbFullVehicleByVehicleId`,
  (vehicleId) => vehicleApi.getKbbFullVehicleByVehicleId({ vehicleId }),
);

export const fetchKbbValuation = createAsyncThunk<
  KbbValuation[],
  { vehicleId: number; zipCode: string; miles: string }
>(`${VEHICLE_REDUCER_KEY}/fetchKbbValuation`, (params) => vehicleApi.getKbbValuation(params));

// BUILD VEHICLE PREVIEW FOR UI
export const buildVehiclePreview = createAsyncThunk<VehiclePreview, VehicleFormState>(
  `${VEHICLE_REDUCER_KEY}/buildVehiclePreview`,
  async (form: VehicleFormState, { dispatch, rejectWithValue }) => {
    const { vin, year, make, model, trim, miles, zipCode, condition, inputMode } = form;

    if (!condition) return rejectWithValue('Missing condition error');

    let fetchedVehicle: KbbFullVehicle | null = null;

    if (inputMode === 'makeModel') {
      if (!(year && make && model && trim)) {
        return rejectWithValue('Missing vehicle configuration');
      }

      const [vehicle] = await dispatch(
        fetchKbbFullVehicle({
          year,
          makeId: make.makeId,
          modelId: model.modelId,
          modelYearId: trim.modelYearId,
          trimId: trim.trimId,
        }),
      ).unwrap();

      fetchedVehicle = vehicle ?? null;
    } else if (inputMode === 'vin') {
      if (!vin) return rejectWithValue('VIN required');

      const [vehicle] = await dispatch(fetchKbbFullVehicleByVin(vin)).unwrap();

      fetchedVehicle = vehicle ?? null;
    } else {
      return rejectWithValue('Input mode is neither makeModel or vin');
    }

    if (!fetchedVehicle) return rejectWithValue('No vehicle returned by KBB');

    const resolvedVehicle = await dispatch(fetchKbbFullVehicleByVehicleId(fetchedVehicle.vehicleId)).unwrap();

    const valuations: KbbValuation[] = await dispatch(
      fetchKbbValuation({
        vehicleId: resolvedVehicle.vehicleId,
        zipCode,
        miles,
      }),
    ).unwrap();

    const tradeInValue = valuations.find(
      (obj) => obj.priceTypeDisplay === `Trade-In Value ${VehicleConditionLabel[condition]}`,
    )?.configuredValue;

    const fairPurchasePrice =
      tradeInValue === undefined
        ? valuations.find((obj) => obj.priceTypeDisplay === `Fair Purchase Price`)?.configuredValue
        : undefined;

    const finalValuation = tradeInValue ?? fairPurchasePrice ?? 0;

    const priceType: VehiclePriceType =
      tradeInValue !== undefined
        ? 'TRADE_IN_VALUE'
        : fairPurchasePrice !== undefined
          ? 'FAIR_PURCHASE_PRICE'
          : 'UNKNOWN';

    const returnObject: VehiclePreview = {
      year: resolvedVehicle.yearId.toString(),
      makeName: resolvedVehicle.makeName,
      modelName: resolvedVehicle.modelName,
      trimName: resolvedVehicle.trimName,
      zipCode,
      miles,
      valuation: finalValuation,
      priceType,
      condition,
      vehicleId: resolvedVehicle.vehicleId,
    };
    return returnObject;
  },
);

export const fetchFullVehicleByVehicleIdOrExistingAccount = createAsyncThunk<
  { resolvedVehicle: KbbFullVehicle; repairedAccount: boolean },
  {
    existingAccount?: VehicleAccountProps;
    kbbVehicleId?: string;
    repairIfNeeded: boolean;
  }
>(
  `${VEHICLE_REDUCER_KEY}/fetchVehicleIds`,
  async ({ existingAccount, kbbVehicleId, repairIfNeeded }, { dispatch, rejectWithValue }) => {
    let resolvedVehicle: KbbFullVehicle | null = null;
    if (kbbVehicleId) {
      resolvedVehicle = await dispatch(fetchKbbFullVehicleByVehicleId(Number(kbbVehicleId))).unwrap();
    } else {
      // Manually find the vehicle if this account does not have a vehicleId
      if (
        existingAccount &&
        isVehicleAccount(existingAccount) &&
        existingAccount?.year &&
        existingAccount?.makeId &&
        existingAccount.modelId &&
        existingAccount.trimId
      ) {
        // This may seem redundant as we need the make/model/trim ids here, but this catches the use case where we are
        // (1) editing an existing account
        // (2) that existing account may have make/model/trim information
        // (3) the existing account may have been disconnected from kbb and does not have a vehicle ID
        // In this case, we pre-populate the vehicle fields but subsequently do not have a modelYearId, which will fail to load a kbb vehicle
        const yearMakeModelIdObj = {
          year: String(existingAccount.year),
          makeId: existingAccount.makeId,
          modelId: existingAccount.modelId,
        };

        const trimsResponse = await dispatch(fetchKbbTrims(yearMakeModelIdObj)).unwrap();

        const foundTrim = trimsResponse.trims.find((trim) => trim.trimId === existingAccount.trimId);
        if (foundTrim) {
          const kbbFullVehicles = await dispatch(
            fetchKbbFullVehicle({
              ...yearMakeModelIdObj,
              trimId: foundTrim.trimId,
              modelYearId: foundTrim.modelYearId,
            }),
          ).unwrap();

          resolvedVehicle = kbbFullVehicles.length > 0 ? kbbFullVehicles[0] : null;
        }
      }
    }

    if (resolvedVehicle) {
      // Check if we need to repair
      let hadRepairedAccount = false;
      const existingAccountId = existingAccount?.id;
      const existingAccountMissingIds =
        !existingAccount?.makeId || !existingAccount?.modelId || !existingAccount?.trimId;
      if (repairIfNeeded && existingAccountId && existingAccountMissingIds) {
        const { makeId, modelId, trimId } = resolvedVehicle;
        const repairedAccount = accountsTypes.mkUpdateAccount('VEHICLE', {
          id: existingAccountId,
          makeId,
          modelId,
          trimId,
        });

        dispatch(accountsActions.updateAccount(repairedAccount));

        hadRepairedAccount = true;
      }

      return {
        resolvedVehicle,
        repairedAccount: hadRepairedAccount,
      };
    } else {
      throw rejectWithValue(
        'Failed to retrieve vehicle information. No vehicle found for this vehicle ID or year/make/model/year combo',
      );
    }
  },
);

export const repairVehicleAccountAndForm = createAsyncThunk<
  { formState: VehicleFormState; repairedAccount: boolean },
  {
    formManager: VehicleFormManager;
    linkTo: string;
    kbbVehicleId?: string;
    possibleExistingVehicleAccount?: VehicleAccountProps | null;
  }
>(
  `${VEHICLE_REDUCER_KEY}/repairVehicleForm`,
  async ({ formManager, linkTo, kbbVehicleId, possibleExistingVehicleAccount }, { dispatch }) => {
    const currentFormState = formManager.runSelector(({ state }) => state);

    // If make/model/trim IDs are already present, no repair needed
    if (
      !possibleExistingVehicleAccount ||
      (currentFormState.make?.makeId && currentFormState.model?.modelId && currentFormState.trim?.trimId)
    ) {
      return { formState: currentFormState, repairedAccount: false };
    }

    const vehicleAndRepair = await dispatch(
      fetchFullVehicleByVehicleIdOrExistingAccount({
        kbbVehicleId,
        existingAccount: possibleExistingVehicleAccount ?? undefined,
        repairIfNeeded: true,
      }),
    ).unwrap();

    const { yearId, makeId, makeName, modelId, modelName, trimId, trimName, modelYearId } =
      vehicleAndRepair.resolvedVehicle;

    const newFormState: VehicleFormState = {
      ...currentFormState,
      linkTo,
      year: String(yearId),
      make: { makeId, makeName },
      model: { modelId, modelName },
      trim: { trimId, trimName, modelYearId },
    };

    const { repairedAccount } = vehicleAndRepair;
    if (repairedAccount) {
      tracker.track(tracker.events.kbbAccountRepairIds);
    }

    formManager.reinit(newFormState);

    return {
      formState: newFormState,
      repairedAccount: repairedAccount,
    };
  },
);
